import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { FadeScreen } from "by/gamefactory/cityversetycoon/scenes/game/components/FadeScreen";
import { gsap } from "gsap";
import { IDestroyOptions, Rectangle, Sprite, Text, TextStyle } from "pixi.js";
import { ICanvasScaler } from "../../../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../../../shared/async/PromiseCompletionSource";
import { GameFont } from "../../../../../common/GameFont";
import { BasePageView } from "../../../../../common/components/BasePageView";
import { ButtonWithScaleAnimation } from "../../../../../common/components/ButtonWithScaleAnimation";
import { IPlayerModel } from "../../../../../common/models/IPlayerModel";
import { SoundsResourcePackageWrapper } from "../../../../../common/resources/SoundsResourcePackageWrapper";
import { InputBlocker } from "../../../../../game/components/InputBlocker";
import { PiggyWheel } from "../../components/piggywheel/PiggyWheel";
import { PiggyWheelSpinButton } from "../../components/piggywheel/PiggyWheelSpinButton";
import { IFortuneWheelController } from "../../controller/IFortuneWheelController";
import { IFortuneWheelModel } from "../../../model/IFortuneWheelModel";
import { HourlyBonusResourcePackage } from "../../resources/HourlyBonusResourcePackage";
import { WedgeRewardData } from "../../../model/WedgeRewardData";
import { WedgeRewardType } from "../../../model/WedgeRewardType";
import { WheelDiceRollsPage } from "./WheelDiceRollsPage";
import { TopPanel } from "../../../../../game/components/toppanel/TopPanel";

export class PiggyWheelPage extends BasePageView {
    private static readonly BACK_TEXT = "PIGGY WHEEL";

    private readonly commonResources: CommonResourcesPackage;
    private readonly wheelResources: HourlyBonusResourcePackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly playerModel: IPlayerModel;
    private readonly fortuneWheelModel: IFortuneWheelModel;
    private readonly fortuneWheelController: IFortuneWheelController;
    private readonly viewCompletionSource: PromiseCompletionSource<void>;
    private readonly topPanel: TopPanel;
    private readonly logger: ILogger;
    private background: Sprite;
    private fadeScreen: FadeScreen;
    private piggyWheel: PiggyWheel;
    private spinButton: PiggyWheelSpinButton;
    private backButton: ButtonWithScaleAnimation;
    private headerText: Text;
    private InputBlocker: InputBlocker;
    private leftLight: Sprite;
    private rightLight: Sprite;

    public constructor(commonResources: CommonResourcesPackage,
        wheelResources: HourlyBonusResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        playerModel: IPlayerModel,
        canvasScaler: ICanvasScaler,
        fortuneWheelModel: IFortuneWheelModel,
        fortuneWheelController: IFortuneWheelController,
        viewCompletionSource: PromiseCompletionSource<void>,
        topPanel: TopPanel,
        logger: ILogger
    ) {
        super(canvasScaler);
        this.commonResources = commonResources;
        this.wheelResources = wheelResources;
        this.soundResources = soundResources;
        this.playerModel = playerModel;
        this.fortuneWheelModel = fortuneWheelModel;
        this.fortuneWheelController = fortuneWheelController;
        this.viewCompletionSource = viewCompletionSource;
        this.topPanel = topPanel;
        this.logger = logger;
        this.build();
        this.once("added", this.show, this);
    }

    protected override build(): void {
        this.background = new Sprite(this.wheelResources.assets_views_hourly_bonus_wheel_popup_bg_png);
        this.background.eventMode = "static";
        this.background.anchor.set(0.5, 0.5);
        this.addChild(this.background);
        this.buildButtons();
        this.fadeScreen = new FadeScreen(0, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.fadeScreen);
        this.piggyWheel = new PiggyWheel(this.wheelResources, this.commonResources, this.soundResources, this.fortuneWheelModel, this.canvasScaler, this.logger);
        this.addChild(this.piggyWheel);
        this.InputBlocker = new InputBlocker();
        this.addChild(this.InputBlocker);
        this.leftLight = new Sprite(this.wheelResources.assets_views_hourly_bonus_piggy_wheel_piggy_wheel_light_png);
        this.addChild(this.leftLight);
        this.rightLight = new Sprite(this.wheelResources.assets_views_hourly_bonus_piggy_wheel_piggy_wheel_light_png);
        this.rightLight.scale.x = -1;
        this.addChild(this.rightLight);
        super.build();
    }

    private buildButtons(): void {
        this.backButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_back_png);
        this.backButton.hitArea = new Rectangle(-this.backButton.width * 0.5, -this.backButton.height, this.backButton.width * 2, this.backButton.height * 2);
        this.backButton.onUp.add(this.onExitClick, this);
        this.addChild(this.backButton);
        this.headerText = new Text(PiggyWheelPage.BACK_TEXT, new BackTextStyle());
        this.headerText.anchor.set(0.5, 0.5);
        this.addChild(this.headerText);
        this.spinButton = new PiggyWheelSpinButton(this.wheelResources, this.soundResources, this.fortuneWheelModel.piggyWheelModel.value.priceInTokens.value);
        this.spinButton.onSpinClicked.add(this.onSpinClick, this);
        this.addChild(this.spinButton);
    }

    private async show(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        this.InputBlocker.blocked = true;
        animationTimeLine.fromTo(this.backButton, { x: this.canvasScaler.getSize().width * 0.9, alpha: 0 }, { x: this.backButton.x, alpha: 1, duration: 0.3, ease: "power3.inout" });
        animationTimeLine.fromTo(this.headerText, { alpha: 0 }, { alpha: 1, duration: 0.1 }, "<80%");
        await animationTimeLine.play();
        animationTimeLine.kill();
        this.InputBlocker.blocked = false;
    }

    private async hidePage(): Promise<void> {
        this.InputBlocker.blocked = true;
        this.piggyWheel.hideWheelFrameAnimation();
        const hideAnimation = new gsap.core.Timeline();
        hideAnimation.to(this, { alpha: 0, duration: 0.3, ease: "power1.out" });
        await hideAnimation.play();
        hideAnimation.kill();
        this.viewCompletionSource.complete();
    }

    private async onSpinClick(): Promise<void> {
        this.InputBlocker.blocked = true;
        const rewardData = await this.fortuneWheelController.claimPiggyWheelReward();
        this.soundResources.assets_sounds_goldenwheel_new_wheel_golden_wheel_spin_button_v2_mp3.play();
        const costInTokens = this.fortuneWheelModel.piggyWheelModel.value.priceInTokens.value;
        this.playerModel.subtractTokens(costInTokens);
        this.piggyWheel.playWheelGlowAnimation();
        await this.piggyWheel.rotateWheel();
        this.topPanel.bringToOriginParent();
        await this.showWheelClaimResultPopup(rewardData);
        this.topPanel.bringToFront();
        this.InputBlocker.blocked = false;
    }

    private async showWheelClaimResultPopup(rewardData: WedgeRewardData): Promise<void> {
        switch (rewardData.type) {
            case WedgeRewardType.DiceRoll:
                const diceRollsPageCS = new PromiseCompletionSource<void>();
                const wheelDiceRollsPage = new WheelDiceRollsPage(
                    this.commonResources,
                    this.wheelResources,
                    this.soundResources,
                    this.canvasScaler,
                    this.playerModel,
                    diceRollsPageCS,
                    rewardData.count
                );
                this.addChild(wheelDiceRollsPage);
                await diceRollsPageCS.result;
                this.removeChild(wheelDiceRollsPage);
                break;
            case WedgeRewardType.Coin:
            case WedgeRewardType.Shield:
            default:
                this.logger.warn(`Reward popup for type: ${rewardData.type} - not implemented.`);
                break;
        }
        this.viewCompletionSource.complete();
    }

    private onExitClick(): void {
        this.soundResources.assets_sounds_upgrade_click_back_arrow_mp3.play();
        this.hidePage();
    }

    protected override layout(width: number, height: number): void {
        if (height * this.canvasScaler.referenceRatio <= width) {
            this.background.width = width;
            this.background.height = width / this.canvasScaler.referenceRatio;
        } else {
            this.background.height = height;
            this.background.width = height * this.canvasScaler.referenceRatio;
        }
        this.background.position.set(width * 0.5, height * 0.5);
        this.InputBlocker.width = width;
        this.InputBlocker.height = height;
        this.background.position.set(width * 0.5, height * 0.5);
        this.backButton.position.set(71 - this.backButton.width * 0.5, 264 - this.backButton.height * 0.5);
        this.headerText.position.set(278, 267);
        this.spinButton.position.set(width * 0.5, 1540);
        this.piggyWheel.position.set(width * 0.5, 800);
        this.leftLight.position.set(0, 449);
        this.rightLight.position.set(width, 449);
        super.layout(width, height);
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.spinButton.onSpinClicked.remove(this.onSpinClick, this);
        this.backButton.onUp.remove(this.onExitClick, this);
        super.destroy(options);
    }
}

class BackTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 60
        }));
    }
}
